import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import SearchableSelect from 'shared/components/SearchableSelect';
import { softwareApi } from '../api/systemRegisterApi';
import { unwrapRecords } from '../utils/responseHelpers';
import {
  AUTHENTICATION_METHOD_OPTIONS,
  DATA_FLOW_DIRECTION,
  INTEGRATION_METHOD_OPTIONS,
  INTEGRATION_STATUS,
  INTEGRATION_TYPE,
  formatEnumLabel,
} from '../constants/enums';
import FormSection from './FormSection';

export const emptyIntegrationRow = () => ({
  key: `int-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  sourceSystem: '',
  targetSystem: '',
  integrationType: '',
  integrationPurpose: '',
  dataFlowDirection: '',
  dataExchanged: '',
  integrationMethod: '',
  authenticationMethod: '',
  integrationOwner: '',
  securityMeasures: '',
  integrationStatus: 'ACTIVE',
});

export default function IntegrationDialog({
  open,
  mode = 'create',
  value,
  saving = false,
  currentSystemName = '',
  onClose,
  onSave,
}) {
  const [draft, setDraft] = useState(emptyIntegrationRow());
  const [registeredNames, setRegisteredNames] = useState([]);

  useEffect(() => {
    if (open) {
      setDraft({ ...emptyIntegrationRow(), ...(value ?? {}) });
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await softwareApi.list({ page: 0, size: 500, status: 'ACTIVE' });
        const { rows } = unwrapRecords(response);
        if (cancelled) return;
        const names = (rows || [])
          .map((s) => String(s.softwareName || '').trim())
          .filter(Boolean);
        setRegisteredNames(names);
      } catch {
        if (!cancelled) setRegisteredNames([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const setField = (name, next) => {
    setDraft((prev) => ({ ...prev, [name]: next }));
  };

  const handleSave = async () => {
    if (!(draft.sourceSystem || '').trim()) {
      toast.error('Source system is required.');
      return;
    }
    if (!(draft.targetSystem || '').trim()) {
      toast.error('Target system is required.');
      return;
    }
    if (saving) return;
    await onSave?.({
      ...draft,
      sourceSystem: draft.sourceSystem.trim(),
      targetSystem: draft.targetSystem.trim(),
      integrationPurpose: (draft.integrationPurpose || '').trim(),
      dataExchanged: (draft.dataExchanged || '').trim(),
      integrationMethod: (draft.integrationMethod || '').trim(),
      authenticationMethod: (draft.authenticationMethod || '').trim(),
      integrationOwner: (draft.integrationOwner || '').trim(),
      securityMeasures: (draft.securityMeasures || '').trim(),
    });
  };

  const textField = (name, label, props = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={draft[name] ?? ''}
      onChange={(e) => setField(name, e.target.value)}
      {...props}
    />
  );

  const enumField = (name, label, options) => (
    <SearchableSelect
      size="medium"
      label={label}
      value={draft[name] || ''}
      onChange={(next) => setField(name, next)}
      options={options.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
    />
  );

  const systemOptions = useMemo(() => {
    const names = new Set(registeredNames);
    const extra = [
      currentSystemName,
      draft.sourceSystem,
      draft.targetSystem,
    ];
    extra.forEach((name) => {
      const trimmed = String(name || '').trim();
      if (trimmed) names.add(trimmed);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [registeredNames, currentSystemName, draft.sourceSystem, draft.targetSystem]);

  const systemField = (name, label) => (
    <Autocomplete
      freeSolo
      fullWidth
      options={systemOptions}
      value={draft[name] || ''}
      onChange={(_, next) => setField(name, typeof next === 'string' ? next : next ?? '')}
      onInputChange={(_, next, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setField(name, next ?? '');
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          required
          label={label}
          placeholder="Select a registered system or type a name"
          helperText="Choose a system already registered, or type the name if it is not in the list."
        />
      )}
    />
  );

  const suggestField = (name, label, options) => (
    <Autocomplete
      freeSolo
      fullWidth
      options={options}
      value={draft[name] || ''}
      onInputChange={(_, next) => setField(name, next ?? '')}
      renderInput={(params) => (
        <TextField {...params} size="small" label={label} />
      )}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{mode === 'edit' ? 'Edit integration' : 'Add integration'}</DialogTitle>
      <DialogContent dividers>
        <FormSection title="Systems">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {systemField('sourceSystem', 'Source system')}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {systemField('targetSystem', 'Target system')}
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Integration details">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {enumField('integrationType', 'Integration type', INTEGRATION_TYPE)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {enumField('dataFlowDirection', 'Data flow direction', DATA_FLOW_DIRECTION)}
            </Grid>
            <Grid size={12}>
              {textField('integrationPurpose', 'Integration purpose', {
                multiline: true,
                minRows: 2,
                placeholder: 'e.g. user authentication, data exchange, remote access',
              })}
            </Grid>
            <Grid size={12}>
              {textField('dataExchanged', 'Data / information exchanged', {
                multiline: true,
                minRows: 2,
                placeholder: 'e.g. employee records, authentication requests, network traffic',
              })}
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Method & security">
          <Grid container spacing={1.5} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {suggestField('integrationMethod', 'Integration method / protocol', INTEGRATION_METHOD_OPTIONS)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {suggestField('authenticationMethod', 'Authentication method', AUTHENTICATION_METHOD_OPTIONS)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {textField('integrationOwner', 'Integration owner / service provider')}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {enumField('integrationStatus', 'Integration status', INTEGRATION_STATUS)}
            </Grid>
            <Grid size={12}>
              {textField('securityMeasures', 'Security measures', {
                multiline: true,
                minRows: 2,
                placeholder: 'e.g. encryption, access controls, MFA, firewall policies, certificates, logging',
              })}
            </Grid>
          </Grid>
        </FormSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
